import { makeAutoObservable } from 'mobx'
import { type IIconsVector } from 'shared/ui'
import {
  type IResponseBillingSource,
  type IResponseBillingSourceAch,
} from 'entities/Billing/api/types'

export const getPaymentMethodLabel = (brand: string): string => {
  if (!brand) return 'Bank Account'

  switch (brand.toLowerCase().replace(' ', '')) {
    case 'visa':
      return 'Visa'
    case 'mastercard':
      return 'Mastercard'
    case 'americanexpress':
    case 'amex':
      return 'American Express'
    case 'discover':
      return 'Discover'
    case 'jcb':
      return 'JCB'
    case 'dinersclub':
      return 'Diners Club'
    case 'unionpay':
      return 'UnionPay'
    default:
      return brand
  }
}

export const getPaymentMethodIcon = (brand: string): IIconsVector => {
  if (!brand) return 'bank'

  switch (brand.toLowerCase().replace(' ', '')) {
    case 'visa':
      return 'visa'
    case 'mastercard':
      return 'mastercard'
    case 'americanexpress':
    case 'amex':
      return 'americanExpress'
    case 'discover':
      return 'discover'
    case 'jcb':
      return 'JCB'
    case 'dinersclub':
      return 'dinersClub'
    case 'unionpay':
      return 'unionPay'
    default:
      return 'bank'
  }
}

export class Source {
  id: string
  object: 'payment_method' | 'card'
  private _brand: string
  private _last4: string
  private _exp_month: number
  private _exp_year: number
  private _primary: boolean
  private _priority: number | null
  franchise_primary: boolean

  constructor(item: IResponseBillingSource | IResponseBillingSourceAch) {
    this.id = item.id
    this.object = item.object

    if (item.object === 'card') {
      this._brand = item.brand || ''
      this._last4 = item.last4
      this._exp_month = item.exp_month
      this._exp_year = item.exp_year
      this._primary = item.primary
      this._priority = item.priority
    } else {
      this._brand = ''
      this._last4 = item.us_bank_account.last4
      this._exp_month = 0
      this._exp_year = 0
      this._primary = false
      this._priority = null
    }
    this.franchise_primary = item.franchise_primary

    makeAutoObservable(this)
  }

  get isMethodAch() {
    return this.object === 'payment_method'
  }

  get isMethodCard() {
    return this.object === 'card'
  }

  get exp() {
    if (this.isMethodAch) return ''

    const exp_month = String(this._exp_month).padStart(2, '0')
    const exp_year = String(this._exp_year).slice(2)

    return `${exp_month}/${exp_year}`
  }

  get name() {
    return `${this.label} .... ${this._last4}`
  }

  get brand() {
    return this._brand
  }

  get last4() {
    return this._last4
  }

  get primary() {
    return this._primary
  }

  get label() {
    if (this.isMethodAch) return 'Chase ACH'

    return getPaymentMethodLabel(this.brand)
  }

  get icon(): IIconsVector {
    return getPaymentMethodIcon(this.brand)
  }

  get isPrimary() {
    return this._primary
  }

  get priority() {
    return this._priority
  }

  setPrimary = (value: boolean) => {
    this._primary = value
  }
}
